import Alpine from "alpinejs";
import { onLargeScreens } from "./mediaQuery";

export const tabData = () => ({
    selected: null,
    tabs: [],
    $popover: null,
    observer: null,
    tabObservers: [],
    tabLabels: [],
    selectMode: "hover",
    liEls: [],
    mq: MediaQueryList,
    initialized: false,

    init() {
        this.mq = onLargeScreens({
            onMatch: this.handleLargeScreens.bind(this),
            onNotMatched: this.handleSmallScreens.bind(this),
        });
    },

    handleLargeScreens() {
        this.selectMode = "hover";
        this.fixTabs();
    },

    handleSmallScreens() {
        this.selectMode = "click";
        this.fixTabs();
    },

    destroy() {
        this.observer.disconnect();
        this.destroyTabObservers();
    },

    setTabs(tabs) {
        this.tabs = tabs;
        this.selected = tabs[0];

        this.tabLabels = tabs.map((tab) => [tab, false]);

        if (this.$refs.tabs) {
            requestAnimationFrame(() => this.initTabs());
        }
    },

    initTabs() {
        this.$refs.tabs
            .querySelectorAll("li:not(.ignore-tab)")
            .forEach((tab) => {
                this.liEls.push(tab);
            });

        this.observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                this.unLazyImages();
                this.initTabObservers();
            } else {
                this.destroyTabObservers();
            }
        });

        this.observer.observe(this.$refs.tabs);

        this.initialized = true;

        this.fixTabs();
    },

    unLazyImages() {
        this.$root.querySelectorAll("img").forEach((img) => {
            img.removeAttribute("loading");
        });
    },

    fixTabs() {
        if (!this.initialized) {
            return;
        }

        if (this.mq.matches) {
            this.fixTabsForLargeScreens();
        } else {
            this.fixTabsForSmallScreens();
        }
    },

    fixTabsForLargeScreens() {
        this.$refs.tabs.parentNode.classList.remove("-mx-4");

        this.liEls.forEach((tab, i) => {
            if (i === 0) {
                tab.classList.remove("pl-4");
            }
        });
    },

    fixTabsForSmallScreens() {
        this.$refs.tabs.querySelector(".tab-buffer")?.remove();

        const isScrolling =
            this.$refs.tabs.scrollWidth >
            this.$refs.tabs.parentNode.getBoundingClientRect().width;

        if (!isScrolling) {
            const bufferNode = document.createElement("li");
            bufferNode.classList.add(
                "border-b",
                "border-sand-light-6-1",
                "lg:hidden",
                "grow",
                "tab-buffer"
            );
            this.$refs.tabs.appendChild(bufferNode);
            return;
        }

        const bufferNode = document.createElement("li");

        bufferNode.style.width = `calc(100% - ${
            this.liEls[this.liEls.length - 1].getBoundingClientRect().width
        }px)`;

        const bufferSpan = document.createElement("span");

        bufferSpan.classList.add(
            "border-b",
            "border-sand-light-6-1",
            "block",
            "h-full"
        );

        bufferNode.appendChild(bufferSpan);
        bufferNode.classList.add("shrink-0", "lg:hidden", "tab-buffer", "pr-4");

        this.$refs.tabs.appendChild(bufferNode);

        this.$refs.tabs.parentNode.classList.add("-mx-4");

        this.liEls.forEach((tab, i) => {
            if (i === 0) {
                tab.classList.add("pl-4");
            }
        });
    },

    initTabObservers() {
        this.liEls.forEach((tab) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        this.tabLabels.forEach((label) => {
                            const span = entry.target.querySelector("button span") as HTMLSpanElement | null;
                            if (span && label[0].label === span.innerText) {
                                label[1] = true;
                            }
                        });
                    } else {
                        this.tabLabels.forEach((label) => {
                            const span = entry.target.querySelector("button span") as HTMLSpanElement | null;
                            if (span && label[0].label === span.innerText) {
                                label[1] = false;
                            }
                        });
                    }

                    const newTab = this.tabLabels.find(
                        (label) => label[1] === true
                    );

                    if (newTab) {
                        this.select(newTab[0]);
                    }
                },
                {
                    root: this.$refs.tabs,
                    threshold: 0.75,
                }
            );

            observer.observe(tab);

            this.tabObservers.push(observer);
        });
    },

    destroyTabObservers() {
        this.tabObservers.forEach((o) => o.disconnect());
    },

    hoverSelect(tab) {
        if (this.selectMode === "hover") {
            this.select(tab);
        }
    },

    clickSelect(tab) {
        this.select(tab);

        this.liEls.forEach((tab) => {
            if (tab.innerText === this.selected.label) {
                tab.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "start",
                });
            }
        });
    },

    select(tab) {
        this.selected = tab;
    },

    selectByLabel(label) {
        this.selected = this.tabs.find((tab) => tab.label === label);
    },

    isSelected(tab) {
        return this.selected === tab;
    },
});

Alpine.data("sideTabs", () => tabData());
